import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { eventsData } from '../../mocks/events';
import SEOHead from '../../components/base/SEOHead';

export default function EventsPage() {
  const upcomingEvents = eventsData.upcoming;
  const recentEvents = eventsData.recent;
  const siteUrl = import.meta.env.VITE_SITE_URL;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": siteUrl
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Events",
            "item": `${siteUrl}/events`
          }
        ]
      },
      {
        "@type": "ItemList",
        "name": "Upcoming Events at King Square Club",
        "description": "Exclusive upcoming events at King Square Club, Benin City's premier luxury nightclub.",
        "url": `${siteUrl}/events`,
        "itemListElement": upcomingEvents.map((event, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Event",
            "name": event.title,
            "description": event.description,
            "startDate": new Date(event.date).toISOString(),
            "endDate": new Date(new Date(event.date).getTime() + 6 * 60 * 60 * 1000).toISOString(),
            "eventStatus": "https://schema.org/EventScheduled",
            "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
            "location": {
              "@type": "NightClub",
              "name": "King Square Club",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "148 Airport Rd",
                "addressLocality": "Benin City",
                "postalCode": "300102",
                "addressRegion": "Edo",
                "addressCountry": "NG"
              }
            },
            "image": event.image,
            "organizer": {
              "@type": "Organization",
              "name": "King Square Club",
              "url": siteUrl
            },
            "offers": {
              "@type": "Offer",
              "url": `${siteUrl}/contact`,
              "price": "1000000",
              "priceCurrency": "NGN",
              "availability": "https://schema.org/InStock",
              "validFrom": new Date().toISOString()
            }
          }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-black">
      <SEOHead
        title="Exclusive Events - King Square Club | Luxury Nightlife in Benin City"
        description="Discover upcoming exclusive events at King Square Club. Premium entertainment, VIP experiences, and unforgettable nights in Benin City's premier luxury nightclub."
        keywords="King Square Club events, Benin City nightclub events, luxury party Benin, VIP events Nigeria, nightlife events Edo, exclusive parties Benin City"
        canonical={`${siteUrl}/events`}
        ogTitle="Exclusive Events at King Square Club"
        ogDescription="Experience unforgettable nights with our curated selection of premium events. Book your VIP table today."
        ogImage={upcomingEvents[0]?.image}
        ogType="website"
        jsonLd={jsonLd}
      />
      <Navbar />
      
      {/* Events Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h1 className="font-cormorant text-5xl md:text-6xl font-bold text-amber-400 mb-4">
              Exclusive Events
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Experience unforgettable nights at King Square Club with our curated selection of premium events
            </p>
          </div>

          {/* Upcoming Events */}
          <div className="mb-20">
            <h3 className="font-cormorant text-3xl md:text-4xl font-bold text-white mb-8 text-center">
              Upcoming Events
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {upcomingEvents.map((event, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-lg bg-zinc-900 hover:bg-zinc-800 transition-all duration-300"
                >
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-3 py-1 bg-amber-400 text-black text-sm font-semibold rounded-full whitespace-nowrap">
                        {event.date}
                      </span>
                    </div>
                    <h4 className="font-cormorant text-2xl font-bold text-white mb-2">
                      {event.title}
                    </h4>
                    <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                      {event.description}
                    </p>
                    <div className="flex items-center justify-end">
                      <Link
                        to="/contact"
                        className="px-4 py-2 bg-amber-400 text-black font-semibold rounded-lg hover:bg-amber-500 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Book Now
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Events */}
          <div>
            <h3 className="font-cormorant text-3xl md:text-4xl font-bold text-white mb-8 text-center">
              Recent Events
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recentEvents.map((event, index) => (
                <div
                  key={index}
                  className="group overflow-hidden rounded-lg bg-zinc-900 hover:bg-zinc-800 transition-all duration-300"
                >
                  <div className="relative h-72 overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-3 py-1 bg-zinc-700 text-gray-300 text-sm font-semibold rounded-full whitespace-nowrap">
                        {event.date}
                      </span>
                    </div>
                    <h4 className="font-cormorant text-2xl font-bold text-white mb-2">
                      {event.title}
                    </h4>
                    <p className="text-gray-300 text-sm line-clamp-2">
                      {event.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-amber-400 text-black font-bold text-lg rounded-lg hover:bg-amber-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              Make a Reservation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
